import { useState } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";

import { DeleteAdminModal } from "@/components/admins/delete-admin-modal";
import { EditAdminModal } from "@/components/admins/edit-admin-modal";
import { useAdmin } from "@/hooks/use-admin";
import { useAdminStore } from "@/stores";
import { Breadcrumbs, Button, ScreenContent, ScreenHeader, Tooltip } from "@/ui";

const AdminDetailPage = () => {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [editAdminModalOpen, setEditAdminModalOpen] = useState(false);
  const [deleteAdminModalOpen, setDeleteAdminModalOpen] = useState(false);

  const { data } = useAdmin().getAdmin(id);
  const currentAdmin = useAdminStore((s) => {
    return s.admin;
  });

  if (!id) {
    return <Navigate to="/admins" />;
  }

  const admin = data?.data;
  const isSelf = !!admin && admin.id === currentAdmin?.id;

  return (
    <>
      <ScreenHeader>
        <Breadcrumbs
          items={[
            { label: "Admins", to: "/admins" },
            { label: admin?.name ?? `Admin #${id}`, to: "/admins/$id", params: { id } },
          ]}
        />

        {/*
          No extra permission check here: admins/layout.tsx already gates this
          whole route tree to super admins, which is the only condition the list
          applies to its own delete action.
        */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              setEditAdminModalOpen(true);
            }}
            variant="secondary"
          >
            Edit
          </Button>

          <Tooltip content="You cannot delete your own account" disabled={!isSelf}>
            <Button
              disabled={!admin || isSelf}
              onClick={() => {
                setDeleteAdminModalOpen(true);
              }}
              variant="danger"
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      </ScreenHeader>

      <ScreenContent>
        <h2 className="text-2xl font-bold text-content-strong">{admin?.name}</h2>
        <p className="text-sm font-medium text-content-strong">{admin?.email}</p>

        <div className="mt-8 grid max-w-3xl grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2 lg:col-start-1">
            <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
              <div className="px-4 py-5 sm:px-6">
                <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <dl className="sm:col-span-1">
                    <dt className="flex items-center text-sm font-medium text-content-muted">
                      Name
                    </dt>
                    <dd className="mt-1 text-sm text-content-strong">{admin?.name}</dd>
                  </dl>
                  <dl className="sm:col-span-1">
                    <dt className="flex items-center text-sm font-medium text-content-muted">
                      Email
                    </dt>
                    <dd className="mt-1 text-sm text-content-strong">{admin?.email}</dd>
                  </dl>
                  <dl className="sm:col-span-1">
                    <dt className="flex items-center text-sm font-medium text-content-muted">
                      Role
                    </dt>
                    <dd className="mt-1 text-sm text-content-strong capitalize">{admin?.role}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScreenContent>

      {admin ? (
        <>
          <EditAdminModal
            admin={admin}
            isOpen={editAdminModalOpen}
            onClose={() => {
              setEditAdminModalOpen(false);
            }}
          />

          <DeleteAdminModal
            admin={admin}
            isOpen={deleteAdminModalOpen}
            onClose={() => {
              setDeleteAdminModalOpen(false);
            }}
            onDeleted={() => {
              return navigate({ to: "/admins" });
            }}
          />
        </>
      ) : null}
    </>
  );
};

export const Route = createFileRoute("/_private/admins/$id/")({ component: AdminDetailPage });
